import { createFeatureSelector, createSelector } from '@ngrx/store';
import { CartState, StoreCart, CartItem } from './cart.models';

export const selectCartState = createFeatureSelector<CartState>('cart');

/**
 * Select all stores in the cart
 */
export const selectCartStores = createSelector(
  selectCartState,
  (state: CartState) => state.stores
);

/**
 * Select cart item count
 */
export const selectCartItemCount = createSelector(
  selectCartState,
  (state: CartState) => state.itemCount
);

/**
 * Select total cart price
 */
export const selectCartTotalPrice = createSelector(
  selectCartState,
  (state: CartState) => state.totalPrice
);

/**
 * Select cart loading state
 */
export const selectCartLoading = createSelector(
  selectCartState,
  (state: CartState) => state.isLoading
);

/**
 * Select cart error
 */
export const selectCartError = createSelector(
  selectCartState,
  (state: CartState) => state.error
);

/**
 * Select all store carts as an array
 */
export const selectStoreCartsArray = createSelector(
  selectCartStores,
  (stores: Record<string, StoreCart>) => Object.values(stores)
);

/**
 * Select cart for a specific store
 */
export const selectStoreCart = (storeId: string) =>
  createSelector(selectCartStores, (stores: Record<string, StoreCart>) => stores[storeId] || null);

/**
 * Select items for a specific store
 */
export const selectStoreCartItems = (storeId: string) =>
  createSelector(
    selectStoreCart(storeId),
    (storeCart: StoreCart | null) => storeCart?.items || []
  );

/**
 * Select total price for a specific store
 */
export const selectStoreCartTotalPrice = (storeId: string) =>
  createSelector(
    selectStoreCart(storeId),
    (storeCart: StoreCart | null) => storeCart?.totalPrice || 0
  );

/**
 * Select total duration for a specific store
 */
export const selectStoreCartTotalDuration = (storeId: string) =>
  createSelector(
    selectStoreCart(storeId),
    (storeCart: StoreCart | null) => storeCart?.totalDuration || 0
  );

/**
 * Select a specific cart item
 */
export const selectCartItem = (storeId: string, itemId: string) =>
  createSelector(
    selectStoreCartItems(storeId),
    (items: CartItem[]) => items.find((item) => item.id === itemId) || null
  );

/**
 * Check if cart is empty
 */
export const selectIsCartEmpty = createSelector(
  selectCartItemCount,
  (itemCount: number) => itemCount === 0
);

/**
 * Check if a specific store has items in cart
 */
export const selectStoreHasItems = (storeId: string) =>
  createSelector(
    selectStoreCartItems(storeId),
    (items: CartItem[]) => items.length > 0
  );

/**
 * Select number of stores in cart
 */
export const selectStoreCount = createSelector(
  selectCartStores,
  (stores: Record<string, StoreCart>) => Object.keys(stores).length
);

